import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";

/**
 * Animal type selection persisted in localStorage.
 * Drives the home dashboard's 12-tile grid and which routes are racing-eligible.
 *
 * Types:
 *   - camel  (الإبل)   → mazayen + races
 *   - horse  (الخيل)   → mazayen + races
 *   - falcon (الصقور)  → mazayen + races
 */
const RACING_TYPES = new Set(["camel", "horse", "falcon"]);

const AnimalTypeContext = createContext(null);

export const ANIMAL_TYPES = [
  { key: "camel",  ar: "الإبل",    en: "Camels",  emoji: "🐪", races: true },
  { key: "horse",  ar: "الخيل",    en: "Horses",  emoji: "🐎", races: true },
  { key: "falcon", ar: "الصقور",   en: "Falcons", emoji: "🦅", races: true },
];

export const AnimalTypeProvider = ({ children }) => {
  const [type, setType] = useState(() => {
    if (typeof window === "undefined") return null;
    const stored = window.localStorage.getItem("thqah_animal_type");
    // Auto-clear deprecated types (sheep/goat were removed) so picker re-opens.
    const valid = ANIMAL_TYPES.some((a) => a.key === stored);
    if (stored && !valid) {
      try { window.localStorage.removeItem("thqah_animal_type"); } catch (_) {}
      return null;
    }
    return stored || null;
  });

  useEffect(() => {
    if (type) {
      try { window.localStorage.setItem("thqah_animal_type", type); } catch (_) {}
    }
  }, [type]);

  const clearType = useCallback(() => {
    setType(null);
    try { window.localStorage.removeItem("thqah_animal_type"); } catch (_) {}
  }, []);

  const supportsRaces = useCallback((t) => RACING_TYPES.has(t || type), [type]);

  const value = useMemo(() => ({ type, setType, clearType, supportsRaces, ANIMAL_TYPES }), [type, clearType, supportsRaces]);

  return <AnimalTypeContext.Provider value={value}>{children}</AnimalTypeContext.Provider>;
};

export const useAnimalType = () => useContext(AnimalTypeContext);
